import { useCreateItineraryMutation } from '@/src/api/rtkQuery/itineraryApi'
import { useLazySrpFareFamilyQuery } from '@/src/api/rtkQuery/srpFareFamilyApi'
import { FareFamilyApiResponse, Plan } from '@/src/components/desktop/srp/fareFamily/types'
import { DESKTOP, MOBILE } from '@/src/constants/deviceTypes'
import useClevertapForSrp from '@/src/hooks/analytics/clevertap/srp'
import { resetAllAIState } from '@/src/redux/slices/aiRecommendationSlice'
import { getSelectedCurrency } from '@/src/redux/slices/currencySlice'
import { useAppDispatch, useAppSelector } from '@/src/redux/store'
import { DeviceType } from '@/src/types/deviceType'
import { FlightCard } from '@/src/types/flight'
import { buildItineraryPayload } from '@/src/utils/itinerary/buildItineraryPayload'
import { getLoadingPagePath } from '@/src/utils/itinerary/getLoadingPagePath'
import { isFareFamilyEnabled } from '@/src/utils/srp/isFareFamilyEnabled'
import { getFareFamilyRequestPayload } from '@/src/utils/srp/searchUtils/getFareFamilyRequest'
import { usePathname, useRouter } from 'next/navigation'
import { useCallback, useEffect, useState } from 'react'
import useSrpGtmEvent from '../../analytics/gtm/srp'
import { useSRPAIFilterManager } from '../aiRecommendation'
import { useRecentSearch } from '../recentSearch'
import { useGetSearchParamsObj } from '../searchParams'
import useSearchResults from '../useSearchResults'
import { useInitialFilterHandler } from './initialFilterHandler'

export function useSrpController ({ deviceType }: { deviceType: DeviceType }) {
    const { urlQueryObj } = useGetSearchParamsObj()
    const selectedCurrency = useAppSelector(getSelectedCurrency)
    // const { formatCurrency } = useFormatCurrencyForSrp()
    // const { formatDate } = useFormatDate()
    // const translations = useTranslations()
    const { jsons, sid, refetch, isUninitialized, query, cards, srpResultsError, srpResultsLoading, invalidUrl } =
        useSearchResults()

    useInitialFilterHandler()
    useSRPAIFilterManager(true)
    const { pushEventToDataLayer } = useSrpGtmEvent()
    const { triggerSrpEvent } = useClevertapForSrp()
    const { setRecentSearch } = useRecentSearch()
    const [showStillAroundPopup, setShowStillAroundPopup] = useState(false)

    //need to refetch on query change even cache data available
    useEffect(() => {
        const isInitialized = !isUninitialized
        if (isInitialized) {
            refetch()
        }
    }, [isUninitialized, query])

    useEffect(() => {
        let id: NodeJS.Timeout | null = null
        if (!srpResultsError && !srpResultsLoading && !invalidUrl) {
            id = setTimeout(
                () => {
                    setShowStillAroundPopup(true)
                },
                10 * 60 * 1000
            ) //ten minutes
            //save to recent search
            setRecentSearch({ queryObj: urlQueryObj, airportList: jsons?.ap || {} })
        }

        return () => {
            setShowStillAroundPopup(false)
            id != null && clearTimeout(id)
        }
    }, [query, srpResultsError, srpResultsLoading, invalidUrl, jsons])

    const dispatch = useAppDispatch()
    const handleReFetchWithSameQuery = useCallback(() => {
        refetch()
        dispatch(resetAllAIState())
    }, [refetch, query])

    const [selectedFlightForFareFamily, setSelectedFlightForFareFamily] = useState<FlightCard | null>(null)
    const [rawFareFamilyData, setRawFareFamilyData] = useState<FareFamilyApiResponse | null>(null) // Store raw API response for transformation in component
    const [isBookingInProgress, setIsBookingInProgress] = useState<boolean>(false) // Track if any booking is in progress
    const [loadingFlightKey, setLoadingFlightKey] = useState<string | null>(null) // Track which specific flight is loading
    const [selectedFlightForFlightDetails, setSelectedFlightForFlightDetails] = useState<FlightCard[] | null>(null)
    const [errorCreateItinerary, setErrorCreateItinerary] = useState<{
        card: FlightCard[]
        fareFamilyData?: (Plan | null)[]
        error: string
    } | null>(null)

    const [createItinerary, { isLoading: isCreateItineraryLoading }] = useCreateItineraryMutation()
    const [triggerFareFamily] = useLazySrpFareFamilyQuery()
    const pathname = usePathname()
    const router = useRouter()

    const handleCreateBooking = useCallback(
        async (flight: FlightCard[], fareFamilyData?: (Plan | null)[]) => {
            pushEventToDataLayer({
                event: 'click_book_flight',
                page_category: 'search_flight'
            })
            setSelectedFlightForFlightDetails(null)
            const fareKey = flight[0].priceBreakup.fare.fk //need to handle for RT
            setIsBookingInProgress(true)
            setLoadingFlightKey(fareKey) // Track which flight is loading
            const localeFromPath = pathname.split('/')[1] || 'en'
            const payload = buildItineraryPayload({
                urlQueryObj,
                flight,
                jsons,
                utmCurrency: selectedCurrency,
                fareFamilyData,
                triggerSrpEvent,
                deviceType,
            })

            if (deviceType === DESKTOP) {
                const loadingPagePath = getLoadingPagePath(localeFromPath)
                const newTab = window.open(loadingPagePath, '_blank')
                try {
                    const response = await createItinerary({ payload }).unwrap()
                    const itineraryId = response?.itineraryId
                    const ancillaryEnabled = response?.meta?.ancillaryEnabled
                    if (itineraryId) {
                        if (newTab) {
                            newTab.location.replace(
                                `/${localeFromPath}/flights/itinerary/${itineraryId}/info?ancillaryEnabled=${ancillaryEnabled}`
                            )
                        } else {
                            // Fallback to same-tab navigation (iPad Pro / popup blocked)
                            router.push(
                                `/${localeFromPath}/flights/itinerary/${itineraryId}/info?ancillaryEnabled=${ancillaryEnabled}`
                            )
                        }
                    }
                } catch (error: any) {
                    if (newTab) {
                        newTab.location.replace(`/flights/itinerary/error`)
                    }
                    triggerSrpEvent('Air_Failures', {
                        action_name: 'create_itinerary_failure',
                        action_type: 'API',
                        action_value: error?.data?.message || 'Failed to create itinerary',
                        flightNumber: flight?.[0]?.airlineCodes?.join('-') || 'N/A'
                    })
                } finally {
                    setIsBookingInProgress(false)
                    setLoadingFlightKey(null)
                }
            } else if (deviceType === MOBILE) {
                try {
                    const response = await createItinerary({ payload }).unwrap()
                    const itineraryId = response?.itineraryId
                    const ancillaryEnabled = response?.meta?.ancillaryEnabled
                    if (itineraryId) {
                        router.push(
                            `/${localeFromPath}/flights/itinerary/${itineraryId}/info?ancillaryEnabled=${ancillaryEnabled}`
                        )
                    }
                } catch (error: any) {
                    setErrorCreateItinerary({
                        card: flight,
                        fareFamilyData: fareFamilyData,
                        error: error?.data?.message || 'Something went wrong. Please try again.'
                    })
                    triggerSrpEvent('Air_Failures', {
                        action_name: 'create_itinerary_failure',
                        action_type: 'API',
                        action_value: error?.data?.message || 'Failed to create itinerary',
                        flightNumber: flight?.[0]?.airlineCodes?.join('-') || 'N/A'
                    })
                } finally {
                    setIsBookingInProgress(false)
                    setLoadingFlightKey(null)
                }
            }
        },
        [createItinerary, deviceType, jsons, pathname, router, urlQueryObj, selectedCurrency]
    )
    const handleRetryCreateBooking = useCallback(() => {
        setErrorCreateItinerary(null)
        if (errorCreateItinerary?.card)
            handleCreateBooking(errorCreateItinerary.card, errorCreateItinerary.fareFamilyData)
    }, [errorCreateItinerary, handleCreateBooking])

    const onViewFlightDetails = useCallback((flights: FlightCard[]) => setSelectedFlightForFlightDetails(flights), [])
    const handleFlightDetailsClose = useCallback(() => setSelectedFlightForFlightDetails(null), [])

    const openFareFamily = (flight: FlightCard, rawData: FareFamilyApiResponse) => {
        setSelectedFlightForFareFamily(flight)
        setRawFareFamilyData(rawData)
    }

    const handleCloseFareFamily = () => {
        setSelectedFlightForFareFamily(null)
        setRawFareFamilyData(null)
    }

    const handleBookClick = useCallback(
        async (flight: FlightCard) => {
            if (isBookingInProgress) return

            const fareKey = flight.priceBreakup.fare.fk
            const cardIndex = cards?.[0]?.findIndex(card => card.id === flight.id) ?? -1

            triggerSrpEvent('Air_UI_Action', {
                action_name: 'book_srp',
                flightNumber: flight.airlineCodes?.join('-') || 'N/A',
                fare_family: isFareFamilyEnabled(flight) ? 'Yes' : 'No',
                card_index: cardIndex + 1,
                action_type: 'card/ fare_modal',
                action_value: 'N/A'
            })

            if (isFareFamilyEnabled(flight)) {
                setIsBookingInProgress(true)
                setLoadingFlightKey(fareKey)
                try {
                    const fareFamilyRequest = getFareFamilyRequestPayload(
                        flight,
                        urlQueryObj,
                        deviceType,
                        sid || null,
                        jsons!
                    )
                    // Use cached data when available (preferCacheValue: true)
                    const { data, error } = await triggerFareFamily(fareFamilyRequest, true)

                    if (error || !data?.responseBody?.data?.solutions) {
                        if (error) {
                            triggerSrpEvent('Air_Failures', {
                                action_name: 'fare_family_fetch_failure',
                                action_type: 'API',
                                action_value:
                                    (error && (error as any).data?.message) ||
                                    (error as any)?.message ||
                                    'Failed to fetch fare family',
                                flightNumber: flight.airlineCodes?.join('-') || 'N/A'
                            })
                        }
                        handleCreateBooking([flight])
                        return
                    }

                    // Check if we have more than 1 plan in the first solution
                    const solutions = data.responseBody.data.solutions
                    if (solutions[0]?.fareFamilyFareRules?.length > 1) {
                        // Multiple plans available, show fare family modal with raw data
                        openFareFamily(flight, data)
                    } else {
                        // Single plan, no plans, or error - proceed with default booking
                        handleCreateBooking([flight])
                    }
                } catch (error) {
                    console.error('Error fetching fare family:', error)
                    handleCreateBooking([flight])
                } finally {
                    setIsBookingInProgress(false)
                    setLoadingFlightKey(null)
                }
            } else {
                handleCreateBooking([flight])
            }
        },
        [
            handleCreateBooking,
            isFareFamilyEnabled,
            openFareFamily,
            urlQueryObj,
            deviceType,
            sid,
            jsons,
            triggerFareFamily,
            isBookingInProgress,
            cards,
            triggerSrpEvent,
            selectedCurrency
        ]
    )

    return {
        selectedFlightForFareFamily,
        setSelectedFlightForFareFamily,
        selectedFlightForFlightDetails,
        setSelectedFlightForFlightDetails,
        handleCreateBooking,
        handleRetryCreateBooking,
        onViewFlightDetails,
        handleFlightDetailsClose,
        handleBookClick,
        rawFareFamilyData,
        setRawFareFamilyData,
        isBookingInProgress,
        loadingFlightKey,
        handleCloseFareFamily,
        showStillAroundPopup,
        handleReFetchWithSameQuery,
        ...(deviceType === MOBILE ? { isCreateItineraryLoading, errorCreateItinerary, setErrorCreateItinerary } : {})
    }
}
export default useSrpController
